import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { validateEmail } from '../lib/validation';

export default function PasswordResetRequest() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const requestResetMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await apiRequest('POST', '/api/password-reset/request', { email });
      return response.json();
    },
    onSuccess: () => {
      setIsSubmitted(true);
    },
    onError: (error: any) => {
      console.error('Password reset request error:', error);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      return;
    }

    requestResetMutation.mutate(email);
  };

  // Use proper email validation
  const emailValidation = validateEmail(email);
  const isValidEmail = emailValidation.valid && email.trim() !== '';

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            </div>
            <CardTitle className="text-2xl text-green-500">Check Your Email</CardTitle>
            <CardDescription className="text-slate-400">
              We've sent a password reset link to your email address
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <Alert className="bg-green-500/10 border-green-500/20">
              <Mail className="h-4 w-4 text-green-400" />
              <AlertDescription className="text-green-400">
                If an account with the email <strong>{email}</strong> exists, 
                we have sent you a password reset link.
              </AlertDescription>
            </Alert>

            <div className="text-sm text-slate-400 space-y-2">
              <p>• Check your inbox and spam folder</p>
              <p>• The link will expire in 15 minutes</p>
              <p>• Didn't receive an email? Try again in a few minutes</p>
            </div>

            <div className="flex flex-col gap-3 pt-4">
              <Button 
                onClick={() => setIsSubmitted(false)}
                variant="outline"
                className="w-full"
              >
                Send Another Email
              </Button>
              
              <Button 
                onClick={() => setLocation('/login')}
                variant="ghost"
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
              <Mail className="h-8 w-8 text-slate-900" />
            </div>
          </div>
          <CardTitle className="text-2xl text-green-500">Reset Your Password</CardTitle>
          <CardDescription className="text-slate-400">
            Enter your email address and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 ${
                  email && !emailValidation.valid ? 'border-red-500 focus:border-red-500' : ''
                }`}
                required
                autoFocus
              />
              {email && !emailValidation.valid && emailValidation.error && (
                <p className="text-sm text-red-400">{emailValidation.error}</p>
              )}
            </div>

            {requestResetMutation.error && (
              <Alert className="bg-red-500/10 border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">
                  {requestResetMutation.error.message || 'Failed to send reset email. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-green-500 hover:bg-green-600 text-slate-900"
              disabled={!isValidEmail || requestResetMutation.isPending}
            >
              {requestResetMutation.isPending ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button 
              onClick={() => setLocation('/login')}
              variant="ghost"
              className="text-slate-400 hover:text-slate-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </div>

          <div className="mt-6 text-xs text-slate-500 text-center space-y-1">
            <p>• Password reset links expire in 15 minutes</p>
            <p>• Check your spam folder if you don't see the email</p>
            <p>• You can only request 3 reset emails every 15 minutes</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
